import { formatDate } from '@folio/lib';
import type { EmailThreadDto } from 'korint-sdk';
import { useTranslation } from 'react-i18next';

const DAY_MS = 24 * 60 * 60 * 1000;
const TIME_ZONE = 'Europe/Paris';

// Paris calendar day as YYYY-MM-DD: the zone formatDate and formatDateWithTime display
const parisDay = new Intl.DateTimeFormat('en-CA', {
  timeZone: TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const calendarDaysAgo = (today: Date, date: Date) =>
  (Date.parse(parisDay.format(today)) - Date.parse(parisDay.format(date))) / DAY_MS;

const AGE_UNITS = [
  { unit: 'year', days: 365 },
  { unit: 'month', days: 30 },
  { unit: 'week', days: 7 },
] as const;

// Null within a week: those dates already read as today, yesterday or a weekday
export const relativeAge = (date: Date, now: Date, language: string) => {
  const days = calendarDaysAgo(now, date);
  const age = AGE_UNITS.find(({ days: unitDays }) => days >= unitDays);
  return age
    ? new Intl.RelativeTimeFormat(language).format(-Math.floor(days / age.days), age.unit)
    : null;
};

export function useInboxDate() {
  const { t, i18n } = useTranslation();
  const time = new Intl.DateTimeFormat(i18n.language, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TIME_ZONE,
  });
  const weekday = new Intl.DateTimeFormat(i18n.language, { weekday: 'short', timeZone: TIME_ZONE });
  const dayMonth = new Intl.DateTimeFormat(i18n.language, {
    day: 'numeric',
    month: 'short',
    timeZone: TIME_ZONE,
  });

  return (iso: string) => {
    const date = new Date(iso);
    const now = new Date();
    const daysAgo = calendarDaysAgo(now, date);

    if (daysAgo <= 0) return t('generalInbox.date.today', { time: time.format(date) });
    if (daysAgo === 1) return t('generalInbox.date.yesterday', { time: time.format(date) });
    if (daysAgo < 7) return `${weekday.format(date)} · ${time.format(date)}`;
    return parisDay.format(date).slice(0, 4) === parisDay.format(now).slice(0, 4)
      ? dayMonth.format(date)
      : formatDate(date, i18n.language);
  };
}

export const counterpartOf = ({
  direction,
  fromAddresses,
  toAddresses,
}: Pick<EmailThreadDto, 'direction' | 'fromAddresses' | 'toAddresses'>) => {
  const counterpart = direction === 'outbound' ? toAddresses[0] : fromAddresses[0];
  return {
    address: counterpart?.address,
    label: counterpart?.name || counterpart?.address || '—',
  };
};

// ponytail: three addresses, one search each; a multi-address search scales past that
const SEARCHED_ADDRESS_COUNT = 3;

export const participantAddresses = (
  emails: Pick<EmailThreadDto, 'fromAddresses' | 'toAddresses'>[],
  mailboxAddress: string,
) =>
  [
    ...new Set(
      emails
        .flatMap(({ fromAddresses, toAddresses }) => [...fromAddresses, ...toAddresses])
        .flatMap(({ address }) => (address ? [address.trim().toLowerCase()] : [])),
    ),
  ]
    .filter(address => address !== mailboxAddress.trim().toLowerCase())
    .slice(0, SEARCHED_ADDRESS_COUNT);

// A shift-click gives the range from the anchor the clicked card's new state, like Gmail
export const toggleSelection = ({
  orderedIds,
  selectedIds,
  anchorId,
  id,
  extendsRange,
}: {
  orderedIds: string[];
  selectedIds: ReadonlySet<string>;
  anchorId: string | null;
  id: string;
  extendsRange: boolean;
}): ReadonlySet<string> => {
  const isSelecting = !selectedIds.has(id);
  const from = extendsRange && anchorId ? orderedIds.indexOf(anchorId) : -1;
  const to = orderedIds.indexOf(id);
  const toggledIds =
    from === -1 ? [id] : orderedIds.slice(Math.min(from, to), Math.max(from, to) + 1);
  const next = new Set(selectedIds);
  for (const toggledId of toggledIds) {
    if (isSelecting) next.add(toggledId);
    else next.delete(toggledId);
  }
  return next;
};

// Stretched over the row so any empty spot opens it; nested controls sit above with `relative z-10`
export const STRETCHED_BUTTON =
  "text-left outline-none after:absolute after:inset-0 after:rounded-md after:content-[''] focus-visible:after:ring-2 focus-visible:after:ring-ring";

// Revealed by the row's `group`; always shown where there is no hover
export const REVEALED_ON_HOVER =
  'shrink-0 text-muted-foreground opacity-0 transition-opacity duration-150 ease-out focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100';
